import * as React from "react";
import styled, { css } from "styled-components";
import { Theme } from "../../../theme";
import { useTheme } from "@material-ui/styles";
import { useMediaQuery, LinearProgress } from "@material-ui/core";
import { MeasurementState } from "../../../hooks/useCameras";
import { DeploymentInfo } from "../../../services/cameraService";
import { CameraOptionsHook } from "../../../hooks/useCameraOptions";
import { overallRisk } from "./measurementRisk";
import { RISK_WEIGHTS, MAX_MEASUREMENTS } from "../../../constants/matrix";

const OPACITY_ALPHA = "80";

const StyledSection = styled.section`
  display: flex;
  flex-direction: row;
  margin-bottom: 8px;
  border: 1px solid #aaa;
  border-radius: 5px;
  background-color: #fff;

  .sideRiskIndicator {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    width: 12px;
  }

  .summaryContent {
    padding: 8px;
    display: flex;
    height: 100%;
    width: 100%;
  }

  .summaryTitle {
    margin-top: -16px;
    margin-bottom: -16px;
    font-weight: 700px;
    font-size: 16px;
    color: #111;
    width: 200px;
    /* height: 36px; */
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    display: block;
  }

  .summaryDescription {
    padding-left: 24px;
    max-width: 300px;
    margin-top: -16px;
    margin-bottom: -16px;
    font-weight: 700px;
    font-size: 12px;
    color: #111;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    display: block;
  }

  .spacer {
    margin-top: -16px;
    margin-bottom: -16px;
    flex: 1;
  }

  .summaryMeasurements {
    margin-top: -12px;
    margin-bottom: -16px;
    width: 240px;
    display: flex;
    flex-direction: row;

    div.measurement {
      margin-right: 4px;
      text-align: justify;
      display: block;
      flex: 1;

      .progressBar {
        margin-top: 20px;
        margin-left: 4px;
        margin-right: 0px;
        width: 42px;
        float: left;
      }

      p.text {
        float: right;
      }

      .clearFix {
        clear: both;
      }
    }
  }

  ${({ theme }: { theme: Theme }) => {
    return css``;
  }};
`;

export interface CameraSummaryProps {
  deployment: DeploymentInfo;
  liveState: MeasurementState | undefined;
  options: CameraOptionsHook["options"];
}

const CameraSummary: React.FC<CameraSummaryProps> = props => {
  const { deployment, liveState, options } = props;
  const theme: Theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  const measurements = {
    density: liveState ? liveState.density : 0,
    mood: liveState ? liveState.mood : 0,
    flow: liveState ? liveState.flow : 0,
  };
  const max = MAX_MEASUREMENTS;

  //Calculate risk color for header
  const riskColor = liveState
    ? overallRisk(liveState, RISK_WEIGHTS).color + OPACITY_ALPHA
    : "#fff";

  const values = [
    { title: "Density", value: measurements.density, max: max.maxDensity },
    { title: "Flow", value: measurements.flow, max: max.maxFlow },
    { title: "Mood", value: measurements.mood, max: max.maxMood },
  ];

  return (
    <>
      <StyledSection theme={theme}>
        <div
          className="sideRiskIndicator"
          style={{ backgroundColor: riskColor }}
        ></div>
        <div className="summaryContent">
          <div className="summaryTitle">
            <p>{deployment.deploymentName}</p>
          </div>
          {mdUp && (
            <div className="summaryDescription">
              <p>{deployment.description}</p>
            </div>
          )}
          <div className="spacer"></div>
          <div className="summaryMeasurements">
            {values.map(v => (
              <div className="measurement">
                <LinearProgress
                  className="progressBar"
                  variant="determinate"
                  value={(v.value / v.max) * 100}
                ></LinearProgress>
                <p className="text">{v.value ? v.value.toFixed(2) : "--"}</p>
                <div className="clearFix"></div>
              </div>
            ))}
          </div>
        </div>
      </StyledSection>
    </>
  );
};

export default CameraSummary;
